import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import { isCompanyAdmin, isCashierLevel } from '@/types';
import { authApi, posApi } from '@/services/api';
import { createCustomerRouted, isLocalFirstMode, listLocalCustomersWithStats } from '@/offline';
import { useSyncStore } from '@/store/syncStore';
import { getApiErrorMessage } from '@/utils/supplierValidation';
import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import { Users, Plus, Search, X, AlertCircle, Menu, FileText, Package, Upload, Activity } from 'lucide-react';
import PakRupeeIcon from '@/components/common/PakRupeeIcon';
import ContactCard from '@/components/common/ContactCard';
import type { POSCustomer } from '@/types';
import Pagination, { LIST_PAGE_SIZE } from '@/components/common/Pagination';
import { CustomerLedgerModal } from '@/components/CustomerLedgerModal';
import PartyLedgerModal from '@/components/PartyLedgerModal';
import SaleInvoiceLedgerModal from '@/components/customers/SaleInvoiceLedgerModal';
import CustomerQuotationsModal from '@/components/customers/CustomerQuotationsModal';
import AddCustomerPaymentModal from '@/components/customers/AddCustomerPaymentModal';
import EntityImportWizard from '@/components/dataImport/EntityImportWizard';
import { customerImportConfig } from '@/components/dataImport/entities/customerImportConfig';
import {
  normalizePhone,
  sanitizePhoneInput,
  validatePhone,
} from '@/utils/mobileValidation';
import { formatCnicOrNtn } from '@/utils/cnicNtnUtils';
import {
  COUNTRIES,
  getCountry,
  getRegionsForCountry,
  isPakistan,
  isGcc,
} from '@/data/locales';
import { lookupUkPostcode } from '@/services/ukPostcode';

type FormErrors = {
  customer_name?: string;
  mobile?: string;
  email?: string;
  cnic?: string;
  opening_balance?: string;
};

interface CityOption {
  name: string;
  city: string;
}

export default function CustomersPage() {
  const { t } = useTranslation();
  const { role } = useAuthStore();
  const isAdmin = isCompanyAdmin(role);
  const { toggleSidebar } = useUIStore();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [showLedger, setShowLedger] = useState(false);
  const [showPartyLedger, setShowPartyLedger] = useState(false);
  const [showInvoiceLedger, setShowInvoiceLedger] = useState(false);
  const [showQuotations, setShowQuotations] = useState(false);
  const [ledgerCustomer, setLedgerCustomer] = useState<string>('');
  const [ledgerCustomerName, setLedgerCustomerName] = useState<string>('');
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [selectedCustomerObj, setSelectedCustomerObj] = useState<any>(null);
  const [editingCustomer, setEditingCustomer] = useState<POSCustomer | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    id: string;
    label: string;
    type: 'customer' | 'supplier' | 'product' | 'variant';
    errorMessage?: string;
  } | null>(null);
  const [formData, setFormData] = useState({
    customer_name: '', mobile: '', email: '', city: '', province: '', postal_code: '', country: 'PK',
    address: '', cnic: '', ntn: '',
    is_default: false,
    customer_type: 'Retailer',
    opening_balance: '',
  });
  const [postcodeError, setPostcodeError] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const qc = useQueryClient();

  useEffect(() => {
    setPage(0);
  }, [search]);

  const posSyncMode = useSyncStore((s) => s.posSyncMode);
  const catalogSyncedAt = useSyncStore((s) => s.catalogSyncedAt);
  const pendingQueueCount = useSyncStore((s) => s.pendingQueueCount);
  const failedQueueCount = useSyncStore((s) => s.failedQueueCount);

  const { data: customersData, isLoading } = useQuery({
    queryKey: ['customers', search, page, posSyncMode, catalogSyncedAt, pendingQueueCount, failedQueueCount],
    queryFn: async () => {
      if (isLocalFirstMode()) {
        const all = await listLocalCustomersWithStats(search || undefined);
        const start = page * LIST_PAGE_SIZE;
        return {
          customers: all.slice(start, start + LIST_PAGE_SIZE) as unknown as POSCustomer[],
          total: all.length,
        };
      }
      const res = await posApi.getCustomers({
        search,
        start: page * LIST_PAGE_SIZE,
        limit: LIST_PAGE_SIZE,
      });
      const message = res.data.message;
      return {
        customers: message.customers as POSCustomer[],
        total: (message.total as number) || 0,
      };
    },
  });

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => { const res = await authApi.getCompanyProfile(); return res.data.message; },
  });
  const defaultCountry = profile?.country || 'PK';

  const { data: cities = [] } = useQuery({
    queryKey: ['cities'],
    queryFn: async () => {
      const res = await posApi.getCities();
      return res.data.message as CityOption[];
    },
  });

  const saveMutation = useMutation({
    mutationFn: (data: unknown) => {
      const payload = editingCustomer
        ? { ...(data as Record<string, unknown>), name: editingCustomer.name, _action: 'update' }
        : { ...(data as Record<string, unknown>), _action: 'create' };
      return createCustomerRouted(payload);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['customers'] }); setShowModal(false); resetForm(); },
    onError: (err: any) => {
      let msg = err.message || "Failed to save customer";
      if (err.response?.data?._server_messages) {
        try {
          msg = JSON.parse(JSON.parse(err.response.data._server_messages)[0]).message;
        } catch(e) {}
      }
      alert(msg);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (name: string) => posApi.deleteCustomer(name),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] });
      setDeleteConfirm(null);
    },
    onError: (err: any) => {
      const errMsg = getApiErrorMessage(err, 'Failed to delete customer');
      setDeleteConfirm((prev) => prev ? { ...prev, errorMessage: errMsg } : null);
    },
  });

  const handleDeleteCustomer = (id: string, label: string) => {
    setDeleteConfirm({ id, label, type: 'customer' });
  };

  const emptyForm = () => ({
    customer_name: '', mobile: '', email: '', city: '', province: '', postal_code: '',
    country: defaultCountry, address: '', cnic: '', ntn: '',
    is_default: false, customer_type: 'Retailer', opening_balance: '',
  });

  const resetForm = () => {
    setFormData(emptyForm());
    setEditingCustomer(null);
    setErrors({});
    setPostcodeError('');
  };

  const openEdit = (customer: POSCustomer) => {
    setEditingCustomer(customer);
    setFormData({
      customer_name: customer.customer_name, mobile: customer.mobile || '',
      email: customer.email || '', city: customer.city || '', province: customer.province || '',
      postal_code: customer.postal_code || '', country: customer.country || defaultCountry,
      address: customer.address || '', cnic: customer.cnic || '', ntn: customer.ntn || '',
      is_default: Boolean(customer.is_default),
      customer_type: customer.customer_type || 'Retailer',
      opening_balance: '',
    });
    setErrors({});
    setPostcodeError('');
    setShowModal(true);
  };

  // ---- Validation rules ----

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validate = (data: typeof formData): FormErrors => {
    const newErrors: FormErrors = {};

    if (!data.customer_name.trim()) {
      newErrors.customer_name = 'Customer name is required.';
    }

    const mobileErr = validatePhone(data.mobile, data.country, { required: false });
    if (mobileErr) {
      newErrors.mobile = mobileErr;
    }

    if (data.email.trim() && !EMAIL_REGEX.test(data.email.trim())) {
      newErrors.email = 'Enter a valid email address.';
    }

    const cnicNtnVal = (data.cnic || data.ntn || '').trim();
    const digits = cnicNtnVal.replace(/\D/g, '');
    if (cnicNtnVal && digits.length !== 13 && digits.length !== 8) {
      newErrors.cnic = 'CNIC must be 13 digits or NTN must be 8 digits.';
    }

    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validate(formData);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const cnicDigits = formData.cnic ? formData.cnic.replace(/\D/g, '') : '';
    const ntnDigits = formData.ntn ? formData.ntn.replace(/\D/g, '') : '';
    const openingBalance = parseFloat(formData.opening_balance) || 0;
    if (!editingCustomer && openingBalance < 0) {
      setErrors({ opening_balance: 'Opening balance cannot be negative.' });
      return;
    }

    const { opening_balance: _openingRaw, ...formFields } = formData;
    const payload: Record<string, unknown> = {
      ...formFields,
      cnic: cnicDigits,
      ntn: ntnDigits,
      mobile: formData.mobile.trim() ? normalizePhone(formData.mobile, formData.country) : '',
    };
    if (!editingCustomer && openingBalance > 0) {
      payload.opening_balance = openingBalance;
    }

    saveMutation.mutate(payload);
  };

  const handleExportData = async () => {
    import('react-hot-toast').then(({ default: toast }) => {
      const toastId = toast.loading('Preparing backup data...');
      posApi.exportCustomersData()
        .then(res => {
          const blob = new Blob([res.data], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          
          const contentDisposition = res.headers['content-disposition'];
          let filename = 'Customers_Export.csv';
          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^"]+)"?/);
            if (match && match[1]) {
              filename = match[1];
            }
          }
          
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          toast.success('Backup downloaded successfully', { id: toastId });
        })
        .catch(err => {
          console.error(err);
          toast.error('Failed to download backup', { id: toastId });
        });
    });
  };

  const customers = customersData?.customers || [];
  const customersTotal = customersData?.total || 0;
  const hasErrors = Object.keys(errors).length > 0;

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <TrialBanner />
        <div className="p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <button onClick={toggleSidebar} className="lg:hidden p-2 -ml-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg shrink-0">
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{t('customers.title')}</h1>
                <p className="text-gray-500 text-sm">{t('customers.subtitle')}</p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => setShowLedger(true)} className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-50 flex items-center gap-2 transition-colors shadow-sm">
                <FileText className="w-4 h-4" /> {t('customers.saleRegister')}
              </button>
              <button
                type="button"
                onClick={() => setShowImportWizard(true)}
                className="btn-secondary flex items-center gap-2"
              >
                <Upload className="w-4 h-4" /> {t('common.import')}
              </button>
              <button
                type="button"
                onClick={handleExportData}
                className="btn-secondary flex items-center gap-2"
              >
                <FileText className="w-4 h-4" /> Backup Data
              </button>
              <button onClick={() => { resetForm(); setShowModal(true); }} className="btn-primary flex items-center gap-2">
                <Plus className="w-4 h-4" /> {t('customers.add')}
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="card p-4 mb-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text" placeholder={t('common.searchNameMobile')}
                className="input-field pl-9" value={search} onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Customers Grid */}
          {isLoading ? (
            <div className="card p-8 text-center text-gray-400">{t('customers.loading')}</div>
          ) : customers.length === 0 ? (
            <div className="card p-8 text-center text-gray-400">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>{t('customers.empty')}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-stretch">
                {customers.map((customer) => {
                  const totalSale = customer.total_sale || 0;
                  const outstanding = customer.outstanding_balance || 0;
                  const received = customer.paid_amount || 0;
                  const progressPercentage = totalSale ? Math.round((received / totalSale) * 100) : 0;

                  return (
                    <ContactCard
                      key={customer.name}
                      name={customer.is_walk_in ? `${customer.customer_name} ${t('customers.walkIn')}` : customer.customer_name}
                      subtitle={customer.customer_type || 'Retailer'}
                      onEdit={!isCashierLevel(role) ? () => openEdit(customer) : undefined}
                      onDelete={isAdmin && !customer.is_walk_in && !customer.is_default ? () => handleDeleteCustomer(customer.name, customer.customer_name) : undefined}
                      onLedger={() => {
                        setLedgerCustomer(customer.name);
                        setLedgerCustomerName(customer.customer_name);
                        setShowPartyLedger(true);
                      }}
                      isDefault={Boolean(customer.is_default)}
                      stats={[
                        {
                          value: totalSale,
                          label: t('customers.totalSales'),
                          icon: Package,
                          iconColorClass: 'text-lime-600',
                          bgClass: 'bg-lime-50'
                        },
                        {
                          value: received,
                          label: t('customers.received'),
                          icon: PakRupeeIcon,
                          iconColorClass: 'text-gray-400',
                          bgClass: 'bg-gray-50'
                        },
                        {
                          value: outstanding,
                          label: t('customers.outstanding'),
                          icon: PakRupeeIcon,
                          iconColorClass: 'text-red-600',
                          bgClass: 'bg-red-50'
                        }
                      ]}
                      progressLabel={t('customers.receivedVsOutstanding')}
                      progressPercentage={progressPercentage}
                      progressSuffix={t('customers.received')}
                      phone={customer.mobile}
                      actions={[
                        {
                          label: 'Register',
                          icon: FileText,
                          onClick: () => { setLedgerCustomer(customer.name); setShowLedger(true); },
                          textClass: 'text-primary-700',
                          bgClass: 'bg-primary-50',
                          hoverClass: 'hover:bg-primary-100'
                        },
                        {
                          label: 'Payment',
                          icon: Activity,
                          onClick: () => { 
                            setSelectedCustomerObj({
                              name: customer.name,
                              customer_name: customer.customer_name,
                              outstanding_balance: outstanding
                            });
                            setShowAddPayment(true); 
                          },
                          textClass: 'text-indigo-700',
                          bgClass: 'bg-indigo-50',
                          hoverClass: 'hover:bg-indigo-100'
                        },
                        {
                          label: t('common.invoices'),
                          icon: FileText,
                          onClick: () => {
                            setLedgerCustomer(customer.name);
                            setLedgerCustomerName(customer.customer_name);
                            setShowInvoiceLedger(true);
                          },
                          textClass: 'text-amber-700',
                          bgClass: 'bg-amber-50',
                          hoverClass: 'hover:bg-amber-100'
                        },
                        {
                          label: 'Quotations',
                          icon: FileText,
                          onClick: () => {
                            setLedgerCustomer(customer.name);
                            setLedgerCustomerName(customer.customer_name);
                            setShowQuotations(true);
                          },
                          textClass: 'text-indigo-700',
                          bgClass: 'bg-indigo-50',
                          hoverClass: 'hover:bg-indigo-100'
                        }
                      ]}
                    />
                  );
                })}
              </div>
              <div className="card overflow-hidden mt-4">
                <Pagination
                  page={page}
                  pageSize={LIST_PAGE_SIZE}
                  total={customersTotal}
                  onPageChange={setPage}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-md max-h-[95vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-100 shrink-0">
              <h3 className="font-semibold text-gray-900">{editingCustomer ? t('customers.edit') : t('customers.add')}</h3>
              <button onClick={() => { setShowModal(false); qc.invalidateQueries({ queryKey: ['customers'] }); }} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-400" /></button>
            </div>

            {/* Error summary at top of modal */}
            {hasErrors && (
              <div className="mx-5 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 shrink-0">
                <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                <ul className="text-sm text-red-600 space-y-0.5">
                  {Object.values(errors).map((msg, i) => (
                    <li key={i}>{msg}</li>
                  ))}
                </ul>
              </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden" noValidate>
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('customers.customerName')} *</label>
                <input
                  required
                  placeholder="e.g. Ahmed Khan"
                  className={`input-field ${errors.customer_name ? 'border-red-400 focus:ring-red-300' : ''}`}
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                />
                {errors.customer_name && <p className="text-xs text-red-500 mt-1">{errors.customer_name}</p>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.country')}</label>
                  <select
                    className="input-field"
                    value={formData.country}
                    onChange={(e) => {
                      const country = e.target.value;
                      setFormData({
                        ...formData,
                        country,
                        province: '',
                        city: '',
                        postal_code: '',
                      });
                      setPostcodeError('');
                    }}
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.region')}</label>
                  <select className="input-field" value={formData.province} onChange={(e) => setFormData({ ...formData, province: e.target.value })}>
                    <option value="">{t('common.select')}</option>
                    {getRegionsForCountry(formData.country).map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {formData.country === 'US' ? t('common.zipCode') : t('common.postalCode')}
                  </label>
                  <div className="flex gap-2">
                    <input
                      className="input-field flex-1"
                      value={formData.postal_code}
                      onChange={(e) => {
                        setPostcodeError('');
                        setFormData({ ...formData, postal_code: e.target.value });
                      }}
                    />
                    {getCountry(formData.country).usePostcodeLookup && (
                      <button
                        type="button"
                        className="btn-secondary text-xs px-3 whitespace-nowrap"
                        onClick={async () => {
                          try {
                            const r = await lookupUkPostcode(formData.postal_code);
                            setFormData((prev) => ({
                              ...prev,
                              postal_code: r.postcode,
                              province: r.province,
                              city: r.city || prev.city,
                            }));
                            setPostcodeError('');
                          } catch (err) {
                            setPostcodeError((err as Error).message || 'Postcode lookup failed');
                          }
                        }}
                      >
                        Find address
                      </button>
                    )}
                  </div>
                  {postcodeError && <p className="text-xs text-red-500 mt-1">{postcodeError}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.city')}</label>
                  {getCountry(formData.country).useCityMaster ? (
                    <select className="input-field" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })}>
                      <option value="">{t('common.select')}</option>
                      {cities.map((c) => (
                        <option key={c.name} value={c.name}>{c.city}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="input-field"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {isGcc(formData.country) ? t('customers.taxIdLabelSa') : t('customers.cnicNtn')}
                  </label>
                  <input
                    placeholder={isGcc(formData.country) ? t('customers.taxIdPlaceholderSa') : 'e.g. 35201-1234567-1 or 1234567-8'}
                    maxLength={15}
                    className={`input-field ${errors.cnic ? 'border-red-400 focus:ring-red-300' : ''}`}
                    value={formatCnicOrNtn(formData.cnic || formData.ntn || '')}
                    onChange={(e) => {
                      const rawVal = e.target.value;
                      const formatted = formatCnicOrNtn(rawVal);
                      const digits = rawVal.replace(/\D/g, '');
                      if (digits.length > 8) {
                        setFormData({ ...formData, cnic: formatted, ntn: '' });
                      } else {
                        setFormData({ ...formData, ntn: formatted, cnic: '' });
                      }
                    }}
                  />
                  {errors.cnic && <p className="text-xs text-red-500 mt-1">{errors.cnic}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.mobile')}</label>
                  <input
                    placeholder={isPakistan(formData.country) ? '03XXXXXXXXX' : t('customers.phonePlaceholder')}
                    maxLength={isPakistan(formData.country) ? 11 : 15}
                    className={`input-field ${errors.mobile ? 'border-red-400 focus:ring-red-300' : ''}`}
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: sanitizePhoneInput(e.target.value, formData.country) })}
                  />
                  {errors.mobile && <p className="text-xs text-red-500 mt-1">{errors.mobile}</p>}
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('customers.customerType')} *</label>
                  <select
                    className="input-field"
                    value={formData.customer_type}
                    onChange={(e) => setFormData({ ...formData, customer_type: e.target.value })}
                  >
                    <option value="Retailer">{t('pos.retailer')}</option>
                    <option value="Wholesale">{t('pos.wholesale')}</option>
                    <option value="Distributor">{t('pos.distributor')}</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2 py-1">
                <input
                  type="checkbox"
                  id="is_default"
                  checked={formData.is_default}
                  onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 h-4 w-4"
                />
                <label htmlFor="is_default" className="text-sm font-medium text-gray-700 select-none">
                  {t('customers.defaultPosCustomer')}
                </label>
              </div>
              {!editingCustomer && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('customers.openingBalance')}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="input-field"
                    placeholder={t('customers.openingBalanceHelp')}
                    value={formData.opening_balance}
                    onChange={(e) => setFormData({ ...formData, opening_balance: e.target.value })}
                    onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                  />
                  {errors.opening_balance && <p className="text-xs text-red-500 mt-1">{errors.opening_balance}</p>}
                </div>
              )}
              </div>
              <div className="shrink-0 border-t border-gray-100 bg-gray-50 p-4 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
                <button type="button" onClick={() => { setShowModal(false); qc.invalidateQueries({ queryKey: ['customers'] }); }} className="btn-secondary w-full sm:flex-1">{t('common.cancel')}</button>
                <button type="submit" disabled={saveMutation.isPending} className="btn-primary w-full sm:flex-1">
                  {saveMutation.isPending ? t('common.saving') : (editingCustomer ? t('common.update') : t('customers.create'))}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <CustomerLedgerModal
        isOpen={showLedger}
        onClose={() => {
          setShowLedger(false);
          setLedgerCustomer('');
          qc.invalidateQueries({ queryKey: ['customers'] });
        }}
        customers={customersData?.customers || []}
        initialCustomer={ledgerCustomer}
      />
      <PartyLedgerModal
        isOpen={showPartyLedger}
        onClose={() => {
          setShowPartyLedger(false);
          setLedgerCustomer('');
          setLedgerCustomerName('');
          qc.invalidateQueries({ queryKey: ['customers'] });
        }}
        partyType="POS Customer"
        party={ledgerCustomer}
        partyName={ledgerCustomerName}
      />
      <SaleInvoiceLedgerModal
        isOpen={showInvoiceLedger}
        onClose={() => {
          setShowInvoiceLedger(false);
          setLedgerCustomer('');
          setLedgerCustomerName('');
          qc.invalidateQueries({ queryKey: ['customers'] });
        }}
        customer={ledgerCustomer}
        customerName={ledgerCustomerName}
      />
      <CustomerQuotationsModal
        isOpen={showQuotations}
        onClose={() => {
          setShowQuotations(false);
          setLedgerCustomer('');
          setLedgerCustomerName('');
          qc.invalidateQueries({ queryKey: ['customers'] });
        }}
        customerId={ledgerCustomer}
        customerName={ledgerCustomerName}
      />
      <AddCustomerPaymentModal
        isOpen={showAddPayment}
        onClose={() => {
          setShowAddPayment(false);
          qc.invalidateQueries({ queryKey: ['customers'] });
        }}
        customer={selectedCustomerObj}
      />

      {showImportWizard && (
        <EntityImportWizard
          config={customerImportConfig}
          onClose={() => {
            setShowImportWizard(false);
            qc.invalidateQueries({ queryKey: ['customers'] });
          }}
          onSuccess={() => qc.invalidateQueries({ queryKey: ['customers'] })}
        />
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${deleteConfirm.errorMessage ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`}>
                <AlertCircle className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-gray-950">
                {deleteConfirm.errorMessage ? t('confirmDelete.cannotDelete') : t('confirmDelete.title')}
              </h3>
            </div>
            
            <p className="text-sm text-gray-600 whitespace-pre-wrap">
              {deleteConfirm.errorMessage || t('confirmDelete.message', { label: deleteConfirm.label })}
            </p>

            <div className="flex justify-end gap-3 pt-2">
              {deleteConfirm.errorMessage ? (
                <button
                  type="button"
                  onClick={() => setDeleteConfirm(null)}
                  className="btn-primary w-24"
                >
                  {t('confirmDelete.ok')}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(null)}
                    className="btn-secondary"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      deleteMutation.mutate(deleteConfirm.id);
                    }}
                    disabled={deleteMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-red-300 rounded-lg transition-colors"
                  >
                    {deleteMutation.isPending ? t('confirmDelete.deleting') : t('common.delete')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}